import ResponsiveStage from "@/components/konva/stage/ResponsiveStage.tsx";
import GameMasterSightMask from "@/tabletop/visibility/GameMasterSightMask.tsx";
import VisibilityPreview from "@/tabletop/visibility/VisibilityPreview.tsx";
import GameMasterPannableLayer from "@/tabletop/GameMasterPannableLayer.tsx";
import TokenLayer from "@/tabletop/tokens/TokenLayer.tsx";
import AreaEffectLayer from "@/tabletop/effects/AreaEffectLayer.tsx";
import AreaEffectHandleLayer from "@/tabletop/effects/AreaEffectHandleLayer.tsx";
import TransitionArrivalMarker from "@/dungeons/geometry/TransitionArrivalMarker.tsx";
import DungeonMapLayer from "./DungeonMapLayer.tsx";
import DungeonGridLayer from "./DungeonGridLayer.tsx";
import DungeonDoorLayer from "./DungeonDoorLayer.tsx";
import DungeonTransitionLayer from "./DungeonTransitionLayer.tsx";
import GameMasterAnnotationLayer from "./GameMasterAnnotationLayer.tsx";
import EncounterLightLayer from "./EncounterLightLayer.tsx";
import type {GameMasterMapModel} from "./useGameMasterMapModel.ts";

export default function GameMasterMapScene({model}: {model: GameMasterMapModel}) {
    const {
        showPlayerSight, showHostileSight, gmMaskOpacity, friendlyVisibility,
        showAnnotations, placementMode, selection, annotations, closeMapPopovers,
        selectDoor, selectLight, placeLight, moveLight, mapTransform, activeLevel,
        arrivalPreview, transitionCenter, targetLevel, setHoveredTransitionId, levelTransition,
        hostileVisibility, toggledLightLocks, canMoveLight,
    } = model;
    return (<ResponsiveStage key="pannable">
            <GameMasterPannableLayer placingAnnotation={placementMode !== null}
                onMapClick={placementMode === "light" ? position => void placeLight(position) : annotations.place}
                onMapPointerDown={closeMapPopovers}>
                <DungeonMapLayer/>
                {showPlayerSight && <GameMasterSightMask visibility={friendlyVisibility}
                    opacity={gmMaskOpacity}/>}
                <DungeonGridLayer screenScale={mapTransform.scale}/>
                {activeLevel && <DungeonTransitionLayer key={activeLevel.id} level={activeLevel}
                    scale={mapTransform.scale} canSelect={levelTransition.canSelect} onSelect={levelTransition.select}
                    onHover={transition => setHoveredTransitionId(transition?.id)}/>}
                <DungeonDoorLayer onSelectDoor={selectDoor}/>
                {showHostileSight && hostileVisibility.enabled &&
                    <VisibilityPreview areas={hostileVisibility.areas}
                        radiusMeters={hostileVisibility.radiusMeters} color="#ef4444"/>}
                <AreaEffectLayer/>
                {showAnnotations && activeLevel && <GameMasterAnnotationLayer
                    annotations={annotations.annotations} grid={activeLevel.geometry.grid}
                    selected={annotations.selected} editing={annotations.editing}
                    onMoveEncounter={annotations.move} onSelect={annotations.select}/>}
                {/* Tokens win normal hit testing where they overlap effects. */}
                <TokenLayer visibility={friendlyVisibility}/>
                <EncounterLightLayer selectedId={selection?.type === "light" ? selection.id : undefined}
                    toggledLightLocks={toggledLightLocks}
                    onSelect={selectLight} onMove={(id, position) => {
                        if (!activeLevel) return;
                        const source = activeLevel.geometry.lights.some(light => light.id === id)
                            ? "dungeon" : "encounter";
                        if (!canMoveLight(source, id)) return;
                        void moveLight(id, position);
                    }}/>
                {/* Only the selected effect's resize handles render above tokens. */}
                <AreaEffectHandleLayer/>
                {arrivalPreview && <TransitionArrivalMarker point={arrivalPreview}
                    origin={transitionCenter} zoom={mapTransform.scale} label={targetLevel?.name}/>}
            </GameMasterPannableLayer>
        </ResponsiveStage>);
}
